import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimeTracking } from '../contexts/TimeTrackingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
    CheckSquare,
    Clock,
    Cloud,
    Sun,
    CloudRain,
    CloudSnow,
    Palmtree,
    StickyNote,
    ChevronRight,
    Plus,
    Play,
    Pause,
    Trophy,
    Coffee,
    X,
    Calendar,
    MapPin,
    Settings,
    Eye,
    EyeOff,
    AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const statusEmoji = {
    open: '🟢',
    in_progress: '🟡',
    review: '🔵',
    overdue: '🔴',
    completed: '🎉'
};

const statusLabel = {
    open: 'Offen',
    in_progress: 'In Arbeit',
    review: 'Review',
    overdue: 'Überfällig',
    completed: 'Erledigt'
};

const weatherIcons = {
    '01d': Sun, '01n': Sun,
    '02d': Cloud, '02n': Cloud,
    '03d': Cloud, '03n': Cloud,
    '04d': Cloud, '04n': Cloud,
    '09d': CloudRain, '09n': CloudRain,
    '10d': CloudRain, '10n': CloudRain,
    '11d': CloudRain, '11n': CloudRain,
    '13d': CloudSnow, '13n': CloudSnow,
    '50d': Cloud, '50n': Cloud
};

const DashboardPage = () => {
    const { user, api } = useAuth();
    const { isTracking, formattedTime, startTracking, stopTracking, cancelTracking } = useTimeTracking();
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    // Mailversand-Warnung (nur Chef/AM): kaputter Absender darf nicht unbemerkt bleiben
    const [mailHealth, setMailHealth] = useState(null);
    useEffect(() => {
        if (!['chef', 'account_manager'].includes(user?.role)) return;
        api.get('/system/mail-status').then(r => setMailHealth(r.data)).catch(() => {});
    }, [user?.role]);  // eslint-disable-line react-hooks/exhaustive-deps
    const [weather, setWeather] = useState(null);
    const [challengePoints, setChallengePoints] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [configMode, setConfigMode] = useState(false);
    const [widgetSettings, setWidgetSettings] = useState({
        showTimer: true,
        showStats: true,
        showTasks: true,
        showWeather: true,
        showCalendar: true,
        showTeam: true
    });
    
    // Stop Timer Modal State
    const [showStopModal, setShowStopModal] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [systemSettings, setSystemSettings] = useState({});
    const [activityTypes, setActivityTypes] = useState([]);
    const [stopForm, setStopForm] = useState({
        customer_id: '',
        task_id: '',
        activity_type: '',
        description: '',
        is_pause: false
    });

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Load widget settings from user preferences
    useEffect(() => {
        const loadWidgetSettings = async () => {
            try {
                const res = await api.get('/users/me/preferences');
                if (res.data?.dashboard_widgets) {
                    setWidgetSettings(prev => ({ ...prev, ...res.data.dashboard_widgets }));
                }
            } catch (e) {}
        };
        loadWidgetSettings();
    }, [api]);

    const saveWidgetSettings = async (newSettings) => {
        setWidgetSettings(newSettings);
        try {
            await api.post('/users/me/preferences', { dashboard_widgets: newSettings });
        } catch (e) {}
    };

    const toggleWidget = (widgetKey) => {
        const newSettings = { ...widgetSettings, [widgetKey]: !widgetSettings[widgetKey] };
        saveWidgetSettings(newSettings);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch dashboard data
                const dashRes = await api.get('/dashboard');
                setDashboard(dashRes.data);
                
                // Fetch other data in parallel but don't fail if one fails
                const [weatherRes, pointsRes, customersRes, tasksRes, settingsRes] = await Promise.allSettled([
                    api.get('/weather', { params: { location: user?.weather_location || 'Vienna,AT' } }),
                    api.get('/challenges/my-points'),
                    api.get('/customers'),
                    api.get('/tasks'),
                    api.get('/system/settings')
                ]);
                
                if (weatherRes.status === 'fulfilled') setWeather(weatherRes.value.data);
                if (pointsRes.status === 'fulfilled') setChallengePoints(pointsRes.value.data?.total_points || 0);
                if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data?.filter(c => !c.is_archived) || []);
                if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value.data?.filter(t => t.status !== 'done') || []);
                if (settingsRes.status === 'fulfilled') {
                    setActivityTypes(settingsRes.value.data?.activity_types || []);
                    setSystemSettings(settingsRes.value.data || {});
                }
            } catch (error) {
                console.error('Failed to fetch dashboard:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [api, user?.weather_location]);

    const handleOpenStopModal = () => {
        setStopForm({
            customer_id: '',
            task_id: '',
            activity_type: '',
            description: '',
            is_pause: false
        });
        setShowStopModal(true);
    };

    const handleStopTimer = async (isPause = false) => {
        try {
            const customer = customers.find(c => c.id === stopForm.customer_id);
            const task = tasks.find(t => t.id === stopForm.task_id);
            
            let description = stopForm.description || '';
            if (stopForm.activity_type) {
                description = `[${stopForm.activity_type}] ${description}`;
            }
            
            await stopTracking(
                customer?.id || null,
                customer?.name || (isPause ? 'Pause' : 'Allgemein'),
                description,
                isPause  // Pass isPause to stopTracking
            );
            
            setShowStopModal(false);
            toast.success(isPause ? 'Pause erfasst' : 'Zeit erfasst');
            
            // Refresh dashboard
            const dashRes = await api.get('/dashboard');
            setDashboard(dashRes.data);
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleCancelTimer = () => {
        cancelTracking();
        setShowStopModal(false);
        toast.info('Timer abgebrochen');
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour >= 5 && hour < 12) return '🌅 Guten Morgen';
        if (hour >= 12 && hour < 18) return '☀️ Guten Tag';
        return '🌙 Guten Abend';
    };

    const WeatherIcon = weather?.icon ? weatherIcons[weather.icon] || Cloud : Cloud;
    const weekProgress = dashboard ? (dashboard.week_hours / dashboard.week_target) * 100 : 0;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto min-h-[calc(100vh-88px)] pb-20 md:pb-8" data-testid="dashboard-page">
            {mailHealth?.broken && (
                <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 p-3 flex gap-3 text-sm" data-testid="mail-broken-banner">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold text-red-600 dark:text-red-400">Mailversand fehlgeschlagen – es gehen gerade keine System-Mails raus</p>
                        <p className="text-muted-foreground mt-0.5">
                            Letzter Fehler {mailHealth.last_error_at ? new Date(mailHealth.last_error_at).toLocaleString('de-AT') : ''}: {mailHealth.last_error}
                        </p>
                        <p className="text-muted-foreground mt-0.5">Betroffen: Login-Links, Tagesübersichten, Passwort-Reset, Rechnungen. Meist ist die Absender-Domain beim Mail-Dienst (Resend) nicht verifiziert.</p>
                    </div>
                </div>
            )}
            {/* Header - groß und prominent */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
            >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {getGreeting()}, {user?.first_name}!
                        </h1>
                        <p className="text-muted-foreground text-sm mt-0.5">
                            {format(currentTime, "EEEE, d. MMMM yyyy", { locale: de })}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            variant={configMode ? "default" : "ghost"}
                            size="icon"
                            onClick={() => setConfigMode(!configMode)}
                            className="rounded-full"
                            title="Dashboard anpassen"
                        >
                            <Settings className="w-5 h-5" />
                        </Button>
                        <div className="text-right">
                            <p className="text-3xl md:text-4xl font-mono font-semibold tracking-tight timer-display">
                                {format(currentTime, 'HH:mm:ss')}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Config Mode Panel */}
                {configMode && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 p-4 bg-muted/50 rounded-lg border"
                    >
                        <p className="text-sm font-medium mb-3">Widgets ein-/ausblenden:</p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { key: 'showTimer', label: 'Timer' },
                                { key: 'showStats', label: 'Statistiken' },
                                { key: 'showTasks', label: 'Tasks' },
                                { key: 'showWeather', label: 'Wetter' },
                                { key: 'showCalendar', label: 'Kalender' },
                                { key: 'showTeam', label: 'Team' },
                            ].map(({ key, label }) => (
                                <Button
                                    key={key}
                                    variant={widgetSettings[key] ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => toggleWidget(key)}
                                    className="gap-2"
                                >
                                    {widgetSettings[key] ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    {label}
                                </Button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </motion.div>

            {/* Bento Grid - kompakte Abstände, normale Schriftgrößen */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Quick Time Tracker */}
                {widgetSettings.showTimer && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200 h-full" data-testid="quick-timer-card">
                        <CardHeader className="pb-2 pt-3 px-4">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Clock className="w-4 h-4 text-primary" />
                                Zeiterfassung
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 pt-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-2xl font-mono font-semibold timer-display">
                                        {isTracking ? formattedTime : '00:00:00'}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {isTracking ? 'Zeit läuft...' : 'Bereit zum Starten'}
                                    </p>
                                </div>
                                <Button
                                    size="default"
                                    className={`rounded-full w-12 h-12 ${isTracking ? 'bg-destructive hover:bg-destructive/90' : ''}`}
                                    onClick={() => isTracking ? handleOpenStopModal() : startTracking()}
                                    data-testid="quick-timer-button"
                                >
                                    {isTracking ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                                </Button>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-border/50">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Heute</span>
                                    <span className="font-medium">{dashboard?.today_hours || 0}h</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Week Stats */}
                {widgetSettings.showStats && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200 h-full" data-testid="week-stats-card">
                        <CardHeader className="pb-2 pt-3 px-4">
                            <CardTitle className="text-base flex items-center gap-2">
                                📊 Wochenübersicht
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 pt-0">
                            <div className="space-y-3">
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-muted-foreground">Wochenstunden</span>
                                        <span className="font-medium">
                                            {dashboard?.week_hours || 0}h / {dashboard?.week_target || 40}h
                                        </span>
                                    </div>
                                    <Progress value={Math.min(weekProgress, 100)} className="h-2" />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-2.5 rounded-lg bg-muted/50">
                                        <p className="text-xl font-semibold">{dashboard?.remaining_vacation_days || 0}</p>
                                        <p className="text-xs text-muted-foreground">🌴 Resturlaub</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-muted/50">
                                        <p className="text-xl font-semibold">{dashboard?.tasks?.length || 0}</p>
                                        <p className="text-xs text-muted-foreground">📋 Offene Tasks</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Weather Widget */}
                {widgetSettings.showWeather && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200 h-full bg-gradient-to-br from-primary/10 to-primary/5" data-testid="weather-card">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-3xl font-semibold">{weather?.temp !== undefined && weather?.temp !== null ? `${weather.temp}°` : '--°'}</p>
                                    <p className="text-sm text-muted-foreground capitalize">{weather?.description || 'Lädt...'}</p>
                                    <p className="text-xs text-muted-foreground mt-1">📍 {weather?.location || 'Vienna'}</p>
                                </div>
                                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                                    <WeatherIcon className="w-7 h-7 text-primary" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Challenge Points Widget */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card 
                        className={`shadow-soft transition-all duration-200 h-full bg-gradient-to-br from-amber-500/10 to-yellow-500/5 ${systemSettings.challenges_enabled !== false ? 'hover:shadow-floating cursor-pointer' : 'opacity-60'}`}
                        data-testid="challenge-points-card"
                        onClick={() => systemSettings.challenges_enabled !== false && navigate('/challenges')}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-3xl font-semibold">{challengePoints}</p>
                                    <p className="text-sm text-muted-foreground">Challenge-Punkte</p>
                                    {systemSettings.challenges_enabled !== false ? (
                                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                            <Trophy className="w-3 h-3" />
                                            Weiter sammeln!
                                        </p>
                                    ) : (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Modul deaktiviert
                                        </p>
                                    )}
                                </div>
                                <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <Trophy className="w-7 h-7 text-amber-500" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Tasks */}
                {widgetSettings.showTasks && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="lg:col-span-2"
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200" data-testid="tasks-card">
                        <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <CheckSquare className="w-4 h-4 text-primary" />
                                Meine Tasks
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')} className="text-primary text-sm h-8">
                                Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 pt-0 min-h-[180px]">
                            {dashboard?.tasks?.length > 0 ? (
                                <div className="space-y-4">
                                    {dashboard.tasks.slice(0, 4).map((task) => (
                                        <motion.div
                                            key={task.id}
                                            whileHover={{ x: 2 }}
                                            className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                                            onClick={() => navigate(`/tasks?task=${task.id}`)}
                                            data-testid={`task-item-${task.id}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-base">{statusEmoji[task.status] || '📋'}</span>
                                                <div>
                                                    <p className="text-sm font-medium truncate max-w-[220px]">{task.title}</p>
                                                    {task.deadline && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {format(new Date(task.deadline), 'd. MMM', { locale: de })}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <span className={`text-xs px-2 py-0.5 rounded-full status-${task.status.replace('_', '-')}`}>
                                                {statusLabel[task.status]}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-muted-foreground h-full flex flex-col items-center justify-center">
                                    <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Keine offenen Tasks</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="mt-2"
                                        onClick={() => navigate('/tasks')}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Task erstellen
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Vacation This Week */}
                {widgetSettings.showTeam && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200 h-full" data-testid="vacation-card">
                        <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Palmtree className="w-4 h-4 text-emerald-500" />
                                Diese Woche abwesend
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 pt-0">
                            {dashboard?.vacations_this_week?.length > 0 ? (
                                <div className="space-y-2">
                                    {dashboard.vacations_this_week.slice(0, 3).map((v) => (
                                        <div 
                                            key={v.id} 
                                            className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10"
                                        >
                                            <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-600 text-sm font-medium">
                                                {v.user_name?.charAt(0) || '?'}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">{v.user_name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {format(new Date(v.start_date), 'd. MMM', { locale: de })} - {format(new Date(v.end_date), 'd. MMM', { locale: de })}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-sm text-center py-4">
                                    👍 Alle da diese Woche!
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Upcoming Calendar Events Widget */}
                {widgetSettings.showCalendar && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.32 }}
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200 h-full bg-gradient-to-br from-red-500/10 to-orange-500/5" data-testid="calendar-widget-card">
                        <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-red-500" />
                                Kommende Termine
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')} className="text-red-500 text-sm h-8">
                                Kalender <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 pt-0">
                            {dashboard?.upcoming_events?.length > 0 ? (
                                <div className="space-y-2">
                                    {dashboard.upcoming_events.slice(0, 3).map((event) => (
                                        <motion.div
                                            key={event.id}
                                            whileHover={{ x: 2 }}
                                            className="flex items-center gap-3 p-2.5 rounded-lg bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 cursor-pointer transition-colors border border-gray-200/50 dark:border-gray-700/50"
                                            onClick={() => navigate('/calendar')}
                                        >
                                            <div 
                                                className="w-2 h-10 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: event.color || '#34c759' }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{event.title}</p>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(event.start), 'EEE, d. MMM HH:mm', { locale: de })}
                                                </div>
                                                {event.location && (
                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                                        <MapPin className="w-3 h-3" />
                                                        <span className="truncate">{event.location}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted-foreground">
                                    <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Keine Termine diese Woche</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="mt-2"
                                        onClick={() => navigate('/calendar')}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Termin erstellen
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
                )}

                {/* Notes */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="lg:col-span-2"
                >
                    <Card className="shadow-soft hover:shadow-floating transition-all duration-200" data-testid="notes-card">
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <StickyNote className="w-5 h-5 text-yellow-500" />
                                Meine Notizen
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/notes')} className="text-primary">
                                Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {dashboard?.notes?.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {dashboard.notes.map((note) => (
                                        <motion.div
                                            key={note.id}
                                            whileHover={{ scale: 1.02 }}
                                            className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 cursor-pointer"
                                            onClick={() => navigate('/notes')}
                                        >
                                            <h4 className="font-medium truncate">{note.title}</h4>
                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{note.content}</p>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                    <p>Keine Notizen vorhanden</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="mt-3"
                                        onClick={() => navigate('/notes')}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Notiz erstellen
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Stop Timer Modal - iOS Style */}
            <Dialog open={showStopModal} onOpenChange={setShowStopModal}>
                <DialogContent className="max-w-md p-0 overflow-hidden">
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                    <span className="text-2xl">⏱️</span>
                                </div>
                                <div>
                                    <p className="font-semibold">Zeit stoppen</p>
                                    <p className="text-3xl font-mono font-bold text-primary">{formattedTime}</p>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    
                    <div className="p-6 pt-2 space-y-5">
                        {/* Quick Actions - iOS Style Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => handleStopTimer(true)}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all hover:scale-[1.02]"
                            >
                                <span className="text-3xl">☕</span>
                                <span className="font-medium text-amber-700 dark:text-amber-400">Pause</span>
                            </button>
                            <button
                                onClick={() => {
                                    setStopForm({ ...stopForm, customer_id: '', task_id: '' });
                                    handleStopTimer(false);
                                }}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20 transition-all hover:scale-[1.02]"
                            >
                                <span className="text-3xl">📋</span>
                                <span className="font-medium text-slate-700 dark:text-slate-400">Allgemein</span>
                            </button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-dashed" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">oder buchen auf</span>
                            </div>
                        </div>

                        {/* Customer Dropdown with Logos */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🏢</span> Kunde
                            </Label>
                            <Select 
                                value={stopForm.customer_id} 
                                onValueChange={(val) => setStopForm({ ...stopForm, customer_id: val })}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Kunde auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {customers.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-7 h-7 rounded-lg">
                                                    {c.logo_url ? (
                                                        <AvatarImage src={c.logo_url} className="object-cover" />
                                                    ) : null}
                                                    <AvatarFallback 
                                                        style={{ backgroundColor: c.ci_primary_color || '#009dde' }} 
                                                        className="text-xs text-white rounded-lg"
                                                    >
                                                        {c.name?.substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="font-medium">{c.name}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Task Dropdown */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>✅</span> Task <span className="text-muted-foreground font-normal">(optional)</span>
                            </Label>
                            <Select 
                                value={stopForm.task_id} 
                                onValueChange={(val) => setStopForm({ ...stopForm, task_id: val })}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Task auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {tasks.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">{t.status === 'done' ? '✅' : '🔘'}</span>
                                                {t.title}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Activity Type Dropdown */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🎯</span> Aktivität
                            </Label>
                            <Select 
                                value={stopForm.activity_type} 
                                onValueChange={(val) => setStopForm({ ...stopForm, activity_type: val })}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Was hast du gemacht?" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activityTypes.map(type => (
                                        <SelectItem key={type} value={type}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">
                                                    {type === 'Grafik' && '🎨'}
                                                    {type === 'Videoschnitt' && '🎬'}
                                                    {type === 'Reporting' && '📊'}
                                                    {type === 'Storyboard' && '📝'}
                                                    {type === 'Entwicklung' && '💻'}
                                                    {type === 'Meeting' && '👥'}
                                                    {type === 'Planung' && '📅'}
                                                    {type === 'Support' && '🛟'}
                                                    {!['Grafik', 'Videoschnitt', 'Reporting', 'Storyboard', 'Entwicklung', 'Meeting', 'Planung', 'Support'].includes(type) && '📌'}
                                                </span>
                                                {type}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Note */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>💬</span> Notiz <span className="text-muted-foreground font-normal">(optional)</span>
                            </Label>
                            <Textarea
                                value={stopForm.description}
                                onChange={(e) => setStopForm({ ...stopForm, description: e.target.value })}
                                placeholder="Kurze Beschreibung deiner Arbeit..."
                                className="mt-2 rounded-xl resize-none"
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex gap-3 p-6 pt-0">
                        <Button 
                            variant="ghost" 
                            onClick={handleCancelTimer} 
                            className="flex-1 h-12 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                            <X className="w-4 h-4 mr-2" />
                            Verwerfen
                        </Button>
                        <Button 
                            onClick={() => handleStopTimer(false)}
                            className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90"
                        >
                            ✨ Zeit speichern
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DashboardPage;
